import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authedHttp, clearSession } from "@/lib/session";
import "@/onboarding.css";
import { Shell } from "@/components/onboarding/Shell";
import { TRACKS, trackQuestions, sectionProgress } from "@/components/onboarding/questions";
import { Intro, DiscoveryWelcome, SectionIntro, QuestionScreen, FinalSummary } from "@/components/onboarding/Screens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = authedHttp(API);

// Step ids: "intro" | "s:<section>" | "<question id>" | "summary"
const buildSteps = (track, role) => {
  const qs = trackQuestions(track, role);
  const steps = ["intro", "welcome"];
  TRACKS[track].sections.forEach((s) => {
    const mine = qs.filter((q) => q.section === s.key);
    if (mine.length) steps.push(`s:${s.key}`, ...mine.map((q) => q.id));
  });
  steps.push("summary");
  return steps;
};

export default function Onboarding({ track = "ceo" }) {
  const T = TRACKS[track];
  const returnTo = encodeURIComponent(T.path);
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [saveState, setSaveState] = useState("saved");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const dirty = useRef(null);
  const cfgRef = useRef(null);
  cfgRef.current = cfg;

  useEffect(() => {
    document.title = track === "tech" ? "Foundation · Technical Discovery" : "Foundation Discovery · Uburu Health";
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    return () => robots.remove();
  }, [track]);

  useEffect(() => {
    (async () => {
      try {
        const me = await http.get("/auth/me");
        setUser(me.data);
        const r = await http.get(`/onboarding/me?track=${track}`);
        setCfg(r.data);
        setPhase("ready");
      } catch (e) {
        const st = e?.response?.status;
        setPhase(st === 401 ? "anon" : st === 403 ? "forbidden" : "error");
      }
    })();
  }, [track]);

  const flush = useCallback(async () => {
    const c = cfgRef.current;
    if (!dirty.current || !c) return;
    const payload = dirty.current;
    dirty.current = null;
    setSaveState("saving");
    try {
      await http.put(`/onboarding/${c.id}`, payload);
      setSaveState("saved");
    } catch (e) {
      if (e?.response?.status === 409) { setSaveState("saved"); return; }
      dirty.current = { ...payload, ...(dirty.current || {}), answers: { ...(payload.answers || {}), ...((dirty.current || {}).answers || {}) } };
      setSaveState("error");
      timer.current = setTimeout(flush, 2500);
    }
  }, []);

  const queue = useCallback((patch) => {
    const prev = dirty.current || {};
    dirty.current = { ...prev, ...patch, answers: { ...(prev.answers || {}), ...(patch.answers || {}) } };
    if (!Object.keys(dirty.current.answers).length) delete dirty.current.answers;
    setSaveState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 900);
  }, [flush]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const steps = cfg ? buildSteps(track, cfg.respondent_role) : ["intro"];
  const requested = params.get("step");
  const step = requested && steps.includes(requested) ? requested : steps.includes(cfg?.current_step) ? cfg.current_step : "intro";

  const go = useCallback((id) => {
    setParams({ step: id });
    setCfg((prev) => ({ ...prev, current_step: id }));
    queue({ current_step: id });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [queue, setParams]);

  const setAnswer = (qid, a) => {
    setCfg((prev) => {
      const next = { ...prev, answers: { ...(prev.answers || {}), [qid]: a } };
      next.progress = sectionProgress(next);
      queue({ answers: { [qid]: a }, progress: next.progress });
      return next;
    });
  };
  const setRole = (role) => {
    setCfg((prev) => { const next = { ...prev, respondent_role: role }; next.progress = sectionProgress(next); queue({ respondent_role: role, progress: next.progress }); return next; });
  };

  const submit = async () => {
    setBusy(true);
    try { clearTimeout(timer.current); await flush(); const r = await http.post(`/onboarding/${cfg.id}/submit`); setCfg(r.data); }
    finally { setBusy(false); }
  };
  const reopen = async () => {
    setBusy(true);
    try { await http.post(`/onboarding/${cfg.id}/reopen`); setCfg((p) => ({ ...p, status: "draft" })); }
    finally { setBusy(false); }
  };
  const signOut = async () => { try { await http.post("/auth/logout"); } finally { clearSession(); nav(`/login-2?return_to=${returnTo}`); } };

  if (phase === "loading") return <div className="fo"><div className="fo-mist" /><div className="fo-centre"><div className="fo-spin" data-testid="onboarding-loading" /></div></div>;
  if (phase === "anon") {
    return (
      <div className="fo"><div className="fo-mist" />
        <div className="fo-centre">
          <div className="fo-card" data-testid="onboarding-signin-gate">
            <p className="fo-eyebrow">{track === "tech" ? "Foundation · Technical Discovery" : "Foundation Discovery"}</p>
            <h1 className="fo-h2" style={{ marginTop: 10 }}>Sign in with your invited email to begin.</h1>
            <p className="muted" style={{ marginTop: 10 }}>We send a one-time code to your email. This link is private to the people Uburu has invited; answers are saved to your account as you go.</p>
            <div style={{ marginTop: 22, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link className="fo-btn" to={`/login-2?return_to=${returnTo}`} data-testid="onboarding-signin">Sign in with a code</Link>
              <Link className="fo-btn2" to="/gateway-2">Back to Crowther</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (phase === "forbidden") {
    return (
      <div className="fo"><div className="fo-mist" />
        <div className="fo-centre">
          <div className="fo-card" data-testid="onboarding-forbidden">
            <p className="fo-eyebrow">Foundation</p>
            <h1 className="fo-h2" style={{ marginTop: 10 }}>This link is for invited email addresses only.</h1>
            <p className="muted" style={{ marginTop: 10 }}>You are signed in as <b data-testid="forbidden-email">{user?.email}</b>, which is not on the invitation list for this session. Sign out and use the email address Uburu was invited with.</p>
            <div style={{ marginTop: 22, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" className="fo-btn" onClick={signOut} data-testid="forbidden-signout">Sign out and switch email</button>
              <Link className="fo-btn2" to="/gateway-2">Back to Crowther</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (phase === "error") return <div className="fo"><div className="fo-mist" /><div className="fo-centre"><div className="fo-card" data-testid="onboarding-error"><h1 className="fo-h2">Foundation could not load your session.</h1><p className="muted" style={{ marginTop: 10 }}>Please refresh. If it keeps happening, tell us.</p></div></div></div>;

  const idx = steps.indexOf(step);
  const back = () => go(steps[Math.max(idx - 1, 0)]);
  const next = () => go(steps[Math.min(idx + 1, steps.length - 1)]);
  const qs = trackQuestions(track, cfg.respondent_role);
  const locked = cfg.status === "submitted";

  let body;
  if (step === "intro") body = <Intro cfg={cfg} onRole={setRole} onBegin={next} />;
  else if (step === "welcome") body = <DiscoveryWelcome track={track} onBegin={next} onBack={back} />;
  else if (step === "summary") body = <FinalSummary cfg={cfg} onGo={go} onSubmit={submit} onReopen={reopen} busy={busy} />;
  else if (step.startsWith("s:")) {
    const s = T.sections.find((x) => x.key === step.slice(2));
    body = <SectionIntro section={s} count={qs.filter((q) => q.section === s.key).length} onContinue={next} onBack={back} />;
  } else {
    const q = qs.find((x) => x.id === step);
    const mine = qs.filter((x) => x.section === q.section);
    body = (
      <>
        {locked && (
          <div className="fo-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }} data-testid="locked-banner">
            <span>This session has been submitted and is read-only.</span>
            <button type="button" className="fo-btn2" onClick={reopen} disabled={busy}>Reopen for editing</button>
          </div>
        )}
        <QuestionScreen q={q} index={mine.indexOf(q) + 1} total={mine.length} answer={cfg.answers?.[q.id]} onAnswer={(a) => setAnswer(q.id, a)} answers={cfg.answers || {}} onBack={back} onNext={next} locked={locked} configId={cfg.id} track={track} />
      </>
    );
  }

  return (
    <div className="fo">
      <div className="fo-mist" />
      <Shell cfg={cfg} user={user} step={step} onSelect={go} saveState={saveState} onSignOut={signOut}>{body}</Shell>
    </div>
  );
}
